// src/pages/EditServicePage.js
// Edit a service listing you've claimed (or self-submitted, which
// auto-claims). Route guard + Firestore rules both check claimedBy —
// this page trusts the rules to do the real enforcement, but checks
// too so people get a clear message instead of a silent Firestore error.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { getService, updateOwnedService } from '../lib/db';
import { storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'health',         label: 'Health & Medical',   emoji: '🏥' },
  { value: 'legal_financial',label: 'Legal & Financial',  emoji: '💼' },
  { value: 'food_drink',     label: 'Food & Drink',       emoji: '🍽️' },
  { value: 'hotels',         label: 'Hotels & Accommodation', emoji: '🏨' },
  { value: 'beauty',         label: 'Beauty & Hair',      emoji: '💇' },
  { value: 'sport_leisure',  label: 'Sport & Leisure',    emoji: '⚽' },
  { value: 'civic',          label: 'Civic & Community',  emoji: '🏛️' },
  { value: 'worship',        label: 'Worship',            emoji: '⛪' },
  { value: 'education',      label: 'Education',          emoji: '🎓' },
  { value: 'transport',      label: 'Transport',          emoji: '🚌' },
  { value: 'home_trade',     label: 'Home & Trade',       emoji: '🔧' },
  { value: 'shopping',       label: 'Shops & Retail',     emoji: '🛍️' },
  { value: 'services',       label: 'Other Services',     emoji: '🏘️' },
];

const EditServicePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notOwner, setNotOwner] = useState(false);
  const [form, setForm] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getService(id).then(svc => {
      if (!svc) { toast.error('Listing not found'); navigate('/services'); return; }
      if (svc.claimedBy !== user?.uid) { setNotOwner(true); setLoading(false); return; }
      setForm({
        name: svc.name || '', category: svc.category || 'services', description: svc.description || '',
        phone: svc.phone || '', email: svc.email || '', website: svc.website || '',
        hours: svc.hours || '', address: svc.address || '',
      });
      setLogoPreview(svc.logoUrl || null);
      setLoading(false);
    });
  }, [id, user, navigate]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2MB'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const updates = {
        name: form.name.trim(), category: form.category, description: form.description.trim(),
        phone: form.phone.trim() || null, email: form.email.trim() || null,
        website: form.website.trim() || null, hours: form.hours.trim() || null,
        address: form.address.trim() || null,
      };
      if (logoFile) {
        const logoRef = ref(storage, `service-logos/${user.uid}/${Date.now()}`);
        await uploadBytes(logoRef, logoFile);
        updates.logoUrl = await getDownloadURL(logoRef);
      }
      await updateOwnedService(id, updates);
      toast.success('Listing updated!');
      navigate('/services');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  if (notOwner) {
    return (
      <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 40px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink)', marginBottom: 8 }}>You haven't claimed this listing</div>
        <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>
          Only the claimed owner can edit this. If this is your business, find it on the Services page and tap "Is this your business?"
        </div>
        <button onClick={() => navigate('/services')} style={{ background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 14, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' }}>
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate(-1)} style={s.backBtn}>← Back</button>
      <div style={s.h1}>Edit Your Listing</div>
      <div style={s.sub}>Changes go live immediately — no need to wait for approval.</div>

      <Field label="Logo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 14, background: 'var(--sage-pale)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
          }}>
            {logoPreview ? (
              <img src={logoPreview} alt="Logo preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 24 }}>🏘️</span>
            )}
          </div>
          <label style={s.uploadBtn}>
            Change logo
            <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
          </label>
        </div>
      </Field>

      <Field label="Category">
        <select style={s.input} value={form.category} onChange={e => set('category', e.target.value)}>
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Name">
        <input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} />
      </Field>

      <Field label="Description">
        <textarea style={{ ...s.input, height: 90, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
      </Field>

      <Field label="Address">
        <input style={s.input} value={form.address} onChange={e => set('address', e.target.value)} />
      </Field>

      <Field label="Opening hours">
        <input style={s.input} value={form.hours} onChange={e => set('hours', e.target.value)} placeholder="e.g. Mon–Fri 9am–5pm" />
      </Field>

      <Field label="Phone">
        <input style={s.input} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
      </Field>

      <Field label="Email">
        <input style={s.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
      </Field>

      <Field label="Website">
        <input style={s.input} value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." />
      </Field>

      <button style={s.submitBtn} disabled={saving} onClick={handleSave}>
        {saving ? 'Saving...' : 'Save changes'}
      </button>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>{label}</div>
    {children}
  </div>
);

const s = {
  backBtn: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 18 },
  h1: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  sub: { fontSize: 13.5, color: 'var(--muted)', fontWeight: 500, marginBottom: 24, lineHeight: 1.5 },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #C8D9C9',
    fontSize: 14, fontFamily: 'Nunito, sans-serif', boxSizing: 'border-box', background: 'var(--white)', color: 'var(--ink)',
  },
  uploadBtn: {
    padding: '10px 16px', borderRadius: 12, border: '1.5px solid var(--sage-light)',
    background: 'var(--white)', color: 'var(--sage-dark)', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
  },
  submitBtn: {
    width: '100%', padding: '15px 16px', borderRadius: 14, marginTop: 8,
    background: 'var(--sage-deep)', color: 'white', border: 'none',
    fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
  },
};

export default EditServicePage;
